import { useEffect, useState } from "react";
import { motion, AnimatePresence, ease as mmEase } from "@/lib/motion";
import { Plus, Trash2, Users, Building2, Smartphone, Wallet as WalletIcon, X, Send, ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { COUNTRIES, CURRENCIES, SEND_METHODS } from "@/lib/constants";
import { initials } from "@/lib/format";

const ez = mmEase.expo;

const METHOD_ICON  = { bank: Building2, mobile_money: Smartphone, wallet: WalletIcon };
const METHOD_COLOR = { bank: "#10B981", mobile_money: "#7C3AED",  wallet: "#06B6D4"  };

const AVATAR_COLORS = ["#10B981", "#7C3AED", "#06B6D4", "#F59E0B", "#EF4444", "#0B1D3A"];

export default function Beneficiaries() {
  const [items,   setItems]   = useState([]);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [form,    setForm]    = useState({
    name: "", country: "NG", currency: "NGN", method: "bank",
    bank_name: "", account_number: "", mobile_number: "", wallet_email: "", nickname: "",
  });

  const load = () =>
    api.get("/beneficiaries")
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await api.post("/beneficiaries", form);
      toast.success(`${form.name} saved`);
      setOpen(false);
      setForm({ ...form, name: "", bank_name: "", account_number: "", mobile_number: "", wallet_email: "", nickname: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save recipient");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/beneficiaries/${id}`);
      toast.success("Recipient removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove recipient");
    }
  };

  return (
    <div className="space-y-6">

      {/* ── Header ──────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -12, filter: "blur(4px)" }}
        animate={{ opacity: 1, y: 0,   filter: "blur(0px)" }}
        transition={{ duration: 0.45, ease: ez }}
        className="flex items-start justify-between gap-4"
      >
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-1">People</p>
          <h1 className="font-display text-[1.65rem] font-black text-navy tracking-tight">Beneficiaries</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Saved recipients — reusable across countries, currencies and methods.
          </p>
        </div>
        <button
          data-testid="bene-add-cta"
          onClick={() => setOpen(true)}
          className="btn-primary shrink-0"
        >
          <Plus size={14} strokeWidth={2.5} /> Add recipient
        </button>
      </motion.div>

      {/* ── Skeleton ────────────────────────────────────────────── */}
      {loading && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="skeleton h-44 rounded-3xl" />
          ))}
        </div>
      )}

      {/* ── Empty state ─────────────────────────────────────────── */}
      {!loading && items.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35, ease: ez }}
          className="card-elevated py-16 text-center"
        >
          <div className="w-14 h-14 rounded-2xl bg-emerald/10 grid place-items-center mx-auto text-emerald mb-4">
            <Users size={22} strokeWidth={1.8} />
          </div>
          <p className="font-display text-xl font-bold text-navy">No recipients yet</p>
          <p className="text-muted-foreground text-sm mt-1 max-w-xs mx-auto">
            Add your first recipient to reuse them on every future transfer.
          </p>
          <button
            data-testid="bene-empty-add"
            onClick={() => setOpen(true)}
            className="btn-navy mt-5 mx-auto"
          >
            <Plus size={14} /> Add recipient
          </button>
        </motion.div>
      )}

      {/* ── Grid ────────────────────────────────────────────────── */}
      {items.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((b, i) => {
            const Icon    = METHOD_ICON[b.method]  || WalletIcon;
            const mColor  = METHOD_COLOR[b.method] || "#10B981";
            const avatar  = AVATAR_COLORS[i % AVATAR_COLORS.length];
            const country = COUNTRIES.find((c) => c.code === b.country);
            return (
              <motion.div
                key={b.id}
                data-testid={`bene-card-${b.id}`}
                className="card-float p-5 group relative overflow-hidden"
                initial={{ opacity: 0, y: 14, scale: 0.96 }}
                animate={{ opacity: 1, y: 0,  scale: 1 }}
                transition={{ duration: 0.4, ease: ez, delay: i * 0.05 }}
                whileHover={{ y: -4 }}
              >
                {/* Ambient glow */}
                <div
                  className="absolute -top-8 -right-8 w-28 h-28 rounded-full blur-2xl opacity-[0.12] group-hover:opacity-[0.22] transition-opacity duration-500 pointer-events-none"
                  style={{ background: avatar }}
                />

                <div className="relative">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-11 h-11 rounded-2xl grid place-items-center text-white font-black text-sm shrink-0"
                        style={{ background: avatar, boxShadow: `0 4px 14px -4px ${avatar}66` }}
                      >
                        {initials(b.name)}
                      </div>
                      <div>
                        <p className="font-semibold text-navy text-sm">{b.name}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {country?.flag} {country?.name || b.country} · {b.currency}
                        </p>
                      </div>
                    </div>
                    <button
                      data-testid={`bene-delete-${b.id}`}
                      onClick={() => remove(b.id)}
                      className="opacity-0 group-hover:opacity-100 w-7 h-7 grid place-items-center rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-all"
                      aria-label="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>

                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-1">
                    <div
                      className="w-5 h-5 rounded-md grid place-items-center shrink-0"
                      style={{ background: `${mColor}14`, color: mColor }}
                    >
                      <Icon size={10} strokeWidth={2} />
                    </div>
                    <span className="capitalize">{b.method?.replace("_", " ")}</span>
                    {b.bank_name && <span>· {b.bank_name}</span>}
                  </div>
                  <p className="text-[11px] font-mono text-navy/60 truncate">
                    {b.account_number || b.mobile_number || b.wallet_email || "—"}
                  </p>

                  <Link
                    to={`/app/send?bene=${b.id}`}
                    className="mt-4 flex items-center justify-center gap-1.5 text-[11px] font-bold py-2 rounded-xl border border-border/60 hover:border-emerald/40 hover:text-emerald hover:bg-emerald/[0.04] text-muted-foreground transition-all"
                  >
                    <Send size={10} strokeWidth={2.2} /> Send money
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── Add modal ───────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm grid place-items-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          >
            <motion.div
              data-testid="bene-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Add a beneficiary"
              className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto relative"
              style={{ boxShadow: "var(--shadow-navy)" }}
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1,    y: 0  }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.32, ease: ez }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header */}
              <div className="brand-gradient px-7 pt-7 pb-6 relative overflow-hidden">
                <div
                  className="absolute -top-10 -right-10 w-36 h-36 rounded-full blur-3xl pointer-events-none"
                  style={{ background: "rgba(16,185,129,0.22)" }}
                />
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="absolute right-4 top-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white/60 hover:text-white transition-all"
                >
                  <X size={16} />
                </button>
                <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-2 relative">
                  New recipient
                </p>
                <h3 className="font-display text-2xl font-black text-white tracking-tight relative">
                  Add a beneficiary
                </h3>
              </div>

              {/* Form */}
              <div className="p-7 space-y-3.5">
                <input
                  data-testid="bene-form-name"
                  placeholder="Full name"
                  value={form.name}
                  onChange={set("name")}
                  className="input-field w-full"
                />
                <div className="grid grid-cols-2 gap-3">
                  <select
                    data-testid="bene-form-country"
                    value={form.country}
                    onChange={set("country")}
                    className="input-field text-navy appearance-none cursor-pointer"
                  >
                    {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
                  </select>
                  <select
                    data-testid="bene-form-currency"
                    value={form.currency}
                    onChange={set("currency")}
                    className="input-field text-navy appearance-none cursor-pointer"
                  >
                    {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
                  </select>
                </div>
                <select
                  data-testid="bene-form-method"
                  value={form.method}
                  onChange={set("method")}
                  className="input-field w-full text-navy appearance-none cursor-pointer"
                >
                  {SEND_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>

                <AnimatePresence mode="wait">
                  {form.method === "bank" && (
                    <motion.div
                      key="bank"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="space-y-3"
                    >
                      <input
                        data-testid="bene-form-bank"
                        placeholder="Bank name"
                        value={form.bank_name}
                        onChange={set("bank_name")}
                        className="input-field w-full"
                      />
                      <input
                        data-testid="bene-form-account"
                        placeholder="Account number"
                        value={form.account_number}
                        onChange={set("account_number")}
                        className="input-field w-full"
                      />
                    </motion.div>
                  )}
                  {form.method === "mobile_money" && (
                    <motion.div
                      key="mobile"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                    >
                      <input
                        data-testid="bene-form-mobile"
                        placeholder="Mobile money number"
                        value={form.mobile_number}
                        onChange={set("mobile_number")}
                        className="input-field w-full"
                      />
                    </motion.div>
                  )}
                  {form.method === "wallet" && (
                    <motion.div
                      key="wallet"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                    >
                      <input
                        data-testid="bene-form-walletemail"
                        placeholder="MoneyMeet email"
                        value={form.wallet_email}
                        onChange={set("wallet_email")}
                        className="input-field w-full"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                <input
                  data-testid="bene-form-nickname"
                  placeholder="Nickname (optional)"
                  value={form.nickname}
                  onChange={set("nickname")}
                  className="input-field w-full"
                />

                <div className="flex gap-3 pt-1">
                  <button
                    data-testid="bene-cancel"
                    onClick={() => setOpen(false)}
                    className="btn-ghost-dark flex-1 justify-center py-3"
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="bene-save"
                    onClick={submit}
                    disabled={saving}
                    className="btn-primary flex-1 justify-center py-3 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {saving
                      ? <><Loader2 size={13} className="animate-spin" /> Saving…</>
                      : <>Save <ArrowRight size={13} /></>}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
